/**
 * Currency affix resolution — the one place the configured symbol is read.
 *
 * Guards the "$575.00$" double-symbol display: `finance.currencyPrefix`
 * defaults to '$', and an admin who wants the French-Canadian trailing symbol
 * typically fills in `finance.currencyPostfix` ('$') WITHOUT clearing the
 * prefix — so the same symbol lands on both sides of every amount. When the
 * postfix merely repeats the prefix symbol we keep the prefix only, so a
 * mis-set config degrades to the conventional "$575.00" instead of a visibly
 * broken amount. A genuinely different postfix (" CAD") is preserved, as is a
 * postfix-only config (prefix cleared).
 *
 * Every money formatter — SystemConfigContext's formatCurrency/formatBalance,
 * the analytics compact formatters, Dashboard's chart axis — must go through
 * here rather than reading the two config fields directly.
 */
export const resolveCurrencyAffixes = (config) => {
  const prefix = config?.finance?.currencyPrefix ?? '$';
  const rawPostfix = config?.finance?.currencyPostfix ?? '';
  const sameSymbol =
    String(prefix).trim() !== ''
    && String(rawPostfix).trim() !== ''
    && String(prefix).trim() === String(rawPostfix).trim();
  return { prefix, postfix: sameSymbol ? '' : rawPostfix };
};

/**
 * Format a WALLET BALANCE (or any signed account amount) with the configured
 * affixes: "-$67.84", "$0.00", "+$10.00" when `signed`. Pure — the one body
 * behind SystemConfigContext.formatBalance (BUG 428).
 *
 * The amount is rounded to whole cents FIRST, sign-symmetrically (half away
 * from zero — the backend's `roundToCents` rule), and the sign is taken from
 * the ROUNDED value: a stored `-67.84000000000003` renders "-$67.84", float
 * noise like -0.000001 can never print "-$0.00", and ±x.xx5 round to the
 * same magnitude. The sign goes BEFORE the prefix ("-$67.84", not "$-67.84").
 */
export const formatBalanceAmount = (amount, config, { signed = false } = {}) => {
  if (amount == null) return '';
  const { prefix, postfix } = resolveCurrencyAffixes(config);
  const n = Number(amount);
  const cents = Math.round(Math.abs(n) * 100);
  const neg = n < 0 && cents > 0;
  const pos = n > 0 && cents > 0;
  const sign = signed ? (pos ? '+' : neg ? '-' : '') : (neg ? '-' : '');
  return `${sign}${prefix}${(cents / 100).toFixed(2)}${postfix}`;
};

export default resolveCurrencyAffixes;
